import React, { useState, useEffect } from "react";
import { Badge, Card, Button, Form } from "react-bootstrap";
import { useAuth } from "../context/AuthProvider";
import {
  uploadMaterial,
  getMaterials,
  materialMatchesAudience,
} from "../services/materialService";
import { getSkills } from "../services/skillService";
import Loader from "../components/Loader";
import MaterialCard from "../components/MaterialCard";
import { toast } from "react-toastify";

export default function Materials() {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState("");
  const [loading, setLoading] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [skills, setSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [audience, setAudience] = useState("student");
  const [activeAudience, setActiveAudience] = useState("student");

  const isAdmin = user?.role === "admin";
  const isTutor = user?.role === "tutor";

  useEffect(() => {
    loadMaterials();
    loadSkills();
  }, []);

  const loadMaterials = async () => {
    setLoading(true);
    try {
      const mats = await getMaterials();
      const visibleMaterials = isTutor
        ? filterMaterialsBySkills(
            (mats || []).filter((material) =>
              materialMatchesAudience(material, "tutor")
            ),
            user?.skills || []
          )
        : mats || [];
      setMaterials(visibleMaterials);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const getSkillId = (skill) => (typeof skill === "string" ? skill : skill?.id);
  const getSkillName = (skill) =>
    typeof skill === "string" ? skill : skill?.name || skill?.id || "";
  const normalizeSkillValue = (value) => String(value || "").trim().toLowerCase();

  const filterMaterialsBySkills = (materialList, userSkills) => {
    const skillKeys = new Set(
      (userSkills || [])
        .flatMap((skill) => [
          normalizeSkillValue(getSkillId(skill)),
          normalizeSkillValue(getSkillName(skill)),
        ])
        .filter(Boolean)
    );

    if (skillKeys.size === 0) return materialList;

    return materialList.filter((material) => {
      const materialSkills = Array.isArray(material.skills) ? material.skills : [];
      const materialSkillIds = Array.isArray(material.skillIds) ? material.skillIds : [];

      if (materialSkills.length === 0 && materialSkillIds.length === 0) {
        return true;
      }

      return [...materialSkills, ...materialSkillIds].some((skill) =>
        skillKeys.has(normalizeSkillValue(getSkillId(skill))) ||
        skillKeys.has(normalizeSkillValue(getSkillName(skill)))
      );
    });
  };

  const loadSkills = async () => {
    try {
      const skillList = await getSkills();
      skillList.sort((a, b) => a.name.localeCompare(b.name));
      setSkills(skillList);
    } catch (error) {
      console.error("Error loading skills:", error);
      setSkills([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id) return toast.error("Please login again to upload materials");
    if (!file) return toast.error("Please select a file to upload");
    if (selectedSkills.length === 0) {
      return toast.error("Please select at least one skill");
    }

    setLoading(true);
    try {
      const tagList = tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      await uploadMaterial({
        file,
        title,
        tags: tagList,
        skills: selectedSkills,
        audience: isAdmin ? audience : "student",
        uploadedBy: { id: user.id, name: user.name },
      });
      toast.success("Material uploaded successfully");
      setFile(null);
      setTitle("");
      setTags("");
      setSelectedSkills([]);
      setAudience("student");
      await loadMaterials();
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to upload material");
    }
    setLoading(false);
  };

  const displayedMaterials = isAdmin
    ? materials.filter((material) => materialMatchesAudience(material, activeAudience))
    : materials;

  return (
    <div className="p-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="mb-1">Materials</h2>
          <p className="text-muted mb-0">
            {isTutor
              ? "Teaching schedules, docs, and tutor resources by skill."
              : "Upload student learning resources or tutor teaching materials skill-wise."}
          </p>
        </div>
      </div>

      {isAdmin && (
        <Card className="mb-4">
          <Card.Body>
            <Card.Title>Upload New Material</Card.Title>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-2">
                <Form.Label>Title</Form.Label>
                <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Optional title" />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Tags (comma separated)</Form.Label>
                <Form.Control value={tags} onChange={(e) => setTags(e.target.value)} placeholder="html, css, layout" />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Material For <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                >
                  <option value="student">Students - learning materials</option>
                  <option value="tutor">Tutors - teaching schedule/docs</option>
                  <option value="all">Students and tutors</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Skills <span className="text-danger">*</span></Form.Label>
                <div className="grid grid-cols-3 gap-2">
                  {skills.map((skill) => (
                    <Form.Check
                      key={skill.id}
                      type="checkbox"
                      label={skill.name}
                      checked={selectedSkills.some((s) => s.id === skill.id)}
                      onChange={(e) =>
                        setSelectedSkills((prev) =>
                          e.target.checked
                            ? prev.some((s) => s.id === skill.id)
                              ? prev
                              : [...prev, skill]
                            : prev.filter((s) => s.id !== skill.id)
                        )
                      }
                    />
                  ))}
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>File</Form.Label>
                <Form.Control
                  type="file"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.mp4,.mov,.webm,.avi,image/*,video/*,application/pdf"
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </Form.Group>

              <div className="d-flex align-items-center">
                <Button type="submit" disabled={loading}>
                  {loading ? <Loader /> : "Upload"}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between gap-3 mb-3 flex-wrap">
            <Card.Title className="mb-0">
              {isTutor ? "Tutoring Materials" : "All Materials"}
            </Card.Title>
            {isAdmin && (
              <div className="d-flex gap-2 flex-wrap">
                {[
                  ["student", "Students"],
                  ["tutor", "Tutors"],
                  ["all", "Both"],
                ].map(([value, label]) => (
                  <Button
                    key={value}
                    size="sm"
                    variant={activeAudience === value ? "primary" : "outline-primary"}
                    onClick={() => setActiveAudience(value)}
                  >
                    {label}
                    <Badge bg={activeAudience === value ? "light" : "primary"} text={activeAudience === value ? "dark" : undefined} className="ms-2">
                      {materials.filter((material) => materialMatchesAudience(material, value)).length}
                    </Badge>
                  </Button>
                ))}
              </div>
            )}
          </div>
          {loading ? (
            <div className="text-center py-4"><Loader /></div>
          ) : displayedMaterials.length === 0 ? (
            <p>No materials yet.</p>
          ) : (
            <div className="row">
              {displayedMaterials.map((material) => (
                <div key={material.id} className="col-md-6 col-xl-4 mb-3">
                  <MaterialCard material={material} showPreviewButton={false} />
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
